import styles from './XpTransactions.module.css';

import classNames from 'classnames';
import { useNavigate } from 'react-router';
import React, { useMemo, useState } from 'react';
import { Wallet, ArrowCounterClockwise } from '@phosphor-icons/react';

import { useUserSession } from 'src/hooks/useUserSession';
import { useDocumentTitle } from 'src/hooks/useDocumentTitle';
import { useQueryScreenState } from 'src/hooks/useQueryScreenState';

import { useXpTransactions } from 'src/queries';

import { Loader } from 'src/components/Loader';
import { Stack } from 'src/components/themed/Stack';
import { Button } from 'src/components/themed/Button';
import { Typography } from 'src/components/themed/Typography';
import { ActionButton } from 'src/components/themed/ActionButton';
import { EmptyState, RequestFailureState } from 'src/components/EmptyState';

import { XpTransactionItem } from '../Profile/components';

export const XpTransactions = () => {
  const [limit] = useState(20);
  const { account, isLoading: isSessionLoading } = useUserSession();
  useDocumentTitle('XP transactions');
  // The ledger is the wallet's (app#155): the api resolves the account session to the account's
  // primary wallet, and an account that has not linked one has no ledger to ask for — it would
  // only be refused (403 WALLET_NOT_LINKED), so it is told instead, with the way to link one. An
  // account the api has not described (a throttled `/user/auth/me`, app#207) is not assumed to
  // have none: the ledger is asked and its own answer decides.
  const hasWallet = !!account?.primaryWalletId;
  const canAsk = account ? hasWallet : !isSessionLoading;
  const query = useXpTransactions(
    {
      limit,
      sortField: 'createdAt',
      sortDirection: 'DESC',
    },
    { enabled: canAsk }
  );
  const { data, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = query;
  const state = useQueryScreenState(query);

  const allItems = useMemo(() => data?.pages.flatMap((page) => page.items) || [], [data?.pages]);

  return (
    <div className={classNames('container', styles.container)}>
      <Stack width="100%" gap={2} p={4} pb={30} maxWidth="750px">
        <Stack
          width="100%"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          pl={4}
        >
          <Typography as="h1" fontWeight="600" variant="title3" color="white">
            XP Transactions
          </Typography>
          {canAsk ? (
            <ActionButton onClick={refetch} label="Refresh transactions">
              <ArrowCounterClockwise size={24} color="white" />
            </ActionButton>
          ) : null}
        </Stack>

        {isSessionLoading ? (
          <Loader />
        ) : !canAsk || state === 'wallet-not-linked' ? (
          <LinkWalletForXp />
        ) : state === 'loading' ? (
          <Loader />
        ) : state !== 'ready' ? (
          <RequestFailureState kind={state} onRetry={() => refetch()} />
        ) : (
          <Stack width="100%" gap={2}>
            {allItems.length > 0 ? (
              <>
                {allItems.map((transaction) => (
                  <XpTransactionItem key={transaction.id} transaction={transaction} />
                ))}
                {hasNextPage ? (
                  <Button
                    onClick={fetchNextPage}
                    title="Load more"
                    loading={isFetchingNextPage}
                    className={styles.loadMore}
                  />
                ) : null}
              </>
            ) : (
              <Stack gap={2} alignItems="center" p={4}>
                <Typography color="lightWhite" textAlign="center">
                  No transactions yet
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </div>
  );
};

/** XP belongs to a wallet, and this account has none yet: the one action goes where one is linked. */
const LinkWalletForXp = () => {
  const navigate = useNavigate();

  return (
    <EmptyState
      icon={<Wallet size={48} />}
      title="Your XP will appear here once a wallet is linked"
      description="XP belongs to a wallet. Link one to your account in Profile."
      action={{ label: 'Link a wallet', onClick: () => navigate('/profile') }}
    />
  );
};
